// src/pages/analytics/CustomerCountsTab.tsx
//
// Customer Counts — distinct buying customers per month by customer
// category (recipe: customer-counts). Matrix of category × month with an
// average column, plus a totalsPerMonth footer (distinct customers overall,
// NOT a column sum — a customer can span categories). A line chart tracks
// the overall count over the window.
//
// Month labels are server-formatted — rendered as-is.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { CustomerCountsRow, CustomerCountsResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const MONTHS_OPTIONS = [6, 13, 24];

function exportMatrix(data: CustomerCountsResponse, exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<CustomerCountsRow> = [
    ['Category', (r) => r.category],
    ...data.months.map((m, i): [string, (r: CustomerCountsRow) => string | number] =>
      [m, (r) => r.counts[i] ?? '']),
    ['Avg', (r) => r.avg],
  ];
  const totalsRow: CustomerCountsRow = {
    category: 'TOTAL (distinct)',
    counts: data.totalsPerMonth,
    avg: data.totalsPerMonth.length > 0
      ? data.totalsPerMonth.reduce((s, v) => s + v, 0) / data.totalsPerMonth.length
      : 0,
  };
  exporter('customer-counts', cols, [...data.rows, totalsRow]);
}

function CountCell({ n }: { n: number }) {
  // Mute zeros so active cells pop.
  if (!n) return <span className="text-slate-700">0</span>;
  return <span className="text-slate-200 tabular-nums">{fmtNum(n)}</span>;
}

export function CustomerCountsTab() {
  const [months, setMonths] = useState(13);
  const resetFilters = () => { setMonths(13); };

  const q = useQuery({
    queryKey: ['analyticsCustomerCounts', months],
    queryFn: () => api.getCustomerCounts({ months }),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const monthLabels = data?.months ?? [];
  const rows = data?.rows ?? [];
  const totalsPerMonth = data?.totalsPerMonth ?? [];

  const chartData = monthLabels.map((m, i) => ({ month: m, customers: totalsPerMonth[i] ?? 0 }));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Users size={20} /> Customer Counts
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Distinct buying customers per month by customer category. The footer counts distinct
          customers overall — a customer buying across categories counts once.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Window</span>
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {MONTHS_OPTIONS.map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => data && exportMatrix(data)}
          disabled={!data || rows.length === 0}
          title="Export the counts matrix to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => data && exportMatrix(data, exportXlsx)}
          disabled={!data || rows.length === 0}
          title="Export the counts matrix to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load customer counts</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* Overall trend line */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Distinct buying customers per month</h3>
            {chartData.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No months to chart</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={16} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      allowDecimals={false} />
                    <Tooltip
                      cursor={{ stroke: '#475569', strokeOpacity: 0.4 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value) => [fmtNum(Number(value)), 'Customers']}
                    />
                    <Line type="monotone" dataKey="customers" stroke="#3b82f6" strokeWidth={2}
                      dot={{ r: 2.5, fill: '#3b82f6' }} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Matrix */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">Category</th>
                    {monthLabels.map((m) => (
                      <th key={m} className="px-3 py-2.5 text-right text-slate-400 font-medium whitespace-nowrap">{m}</th>
                    ))}
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium border-l border-slate-700/40">Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.category}-${i}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[240px] truncate" title={r.category}>{r.category}</td>
                      {r.counts.map((c, ci) => (
                        <td key={ci} className="px-3 py-2 text-right"><CountCell n={c} /></td>
                      ))}
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums border-l border-slate-800/60">{r.avg.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white whitespace-nowrap">Total (distinct)</td>
                      {totalsPerMonth.map((t, ti) => (
                        <td key={ti} className="px-3 py-2 text-right text-white tabular-nums">{fmtNum(t)}</td>
                      ))}
                      <td className="px-3 py-2 text-right border-l border-slate-700/60" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {rows.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
